import React, { useState } from 'react';
import { 
  Database, 
  ShieldCheck, 
  Activity, 
  RefreshCw, 
  Layers, 
  Globe2, 
  BookOpen, 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  Clock, 
  FileCode2, 
  ArrowRight, 
  Lock, 
  Server, 
  Check, 
  Copy, 
  ExternalLink,
  ChevronRight,
  Filter,
  Search,
  FileCheck2,
  GitBranch,
  TableProperties
} from 'lucide-react';
import { 
  DELIVERABLES_P3, 
  DATA_SOURCES_CATALOG, 
  INGESTION_RUNS_DATA, 
  DATA_QUARANTINE_DATA, 
  DATA_QUALITY_RULES_DATA, 
  FRESHNESS_POLICIES_DATA, 
  TAX_RULESETS_DATA, 
  DATA_CONFLICTS_DATA, 
  DATA_INCIDENTS_DATA, 
  GLOBAL_MARKET_READINESS_DATA, 
  SAMPLE_DATA_LINEAGE_NODES,
  POSTGRES_SCHEMAS_P3
} from '../../data/governanceData';
import { DataQuarantineItem } from '../../types';

type GovernanceTab = 
  | 'overview' 
  | 'schemas' 
  | 'quality' 
  | 'freshness' 
  | 'lineage' 
  | 'coverage' 
  | 'sources' 
  | 'governance' 
  | 'master-specs';

interface DataGovernanceViewProps {
  onNavigateHome: () => void;
  onNavigateTools: () => void;
  onNavigateArchitecture: () => void;
}

export const DataGovernanceView: React.FC<DataGovernanceViewProps> = ({
  onNavigateHome,
  onNavigateTools,
  onNavigateArchitecture
}) => {
  const [activeTab, setActiveTab] = useState<GovernanceTab>('overview');
  const [selectedSchema, setSelectedSchema] = useState<number>(0);
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [copiedCode, setCopiedCode] = useState<boolean>(false);
  const [quarantineItems, setQuarantineItems] = useState<DataQuarantineItem[]>(DATA_QUARANTINE_DATA);
  const [resolvedNotice, setResolvedNotice] = useState<string | null>(null);

  const handleCopySQL = (sqlText: string) => {
    navigator.clipboard.writeText(sqlText);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleResolveQuarantine = (id: string, action: 'RESOLVED' | 'REJECTED') => {
    setQuarantineItems(prev => prev.map(item => {
      if (item.id === id) {
        return {
          ...item,
          status: action,
          resolvedBy: 'security.admin@yamoopay.com',
          resolvedAt: new Date().toISOString().replace('T', ' ').substring(0, 19) + ' UTC',
          resolutionNotes: action === 'RESOLVED' 
            ? 'Verified by operator. Corrected schema and manually promoted to canonical.'
            : 'Discarded payload permanently after operator review.'
        };
      }
      return item;
    }));

    setResolvedNotice(`Item ${id} successfully marked as ${action}.`);
    setTimeout(() => setResolvedNotice(null), 3500);
  };

  const filteredDeliverables = DELIVERABLES_P3.filter(item => {
    const matchesCategory = selectedCategory === 'ALL' || item.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      item.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.titleEn.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.titleFr.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.summary.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="w-full bg-[#FBFBFC] text-[#1E2022] min-h-screen">
      {/* Top Banner / Breadcrumb */}
      <div className="border-b border-[#E4E4E8] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-2 text-[#72777A]">
            <button 
              onClick={onNavigateHome}
              className="hover:text-[#1E2022] transition-colors cursor-pointer font-medium"
            >
              YamooPay
            </button>
            <span>/</span>
            <button 
              onClick={onNavigateArchitecture}
              className="hover:text-[#1E2022] transition-colors cursor-pointer"
            >
              Architecture Explorer
            </button>
            <span>/</span>
            <span className="text-[#1E2022] font-semibold">Data Trust & Governance Center</span>
          </div>

          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-[#EBF8F2] text-[#0E7044] border border-[#B3E8CE]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0E7044] animate-pulse"></span>
              PostgreSQL 18 Ready
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-[#F0F4FF] text-[#1E40AF] border border-[#C7D7FE]">
              <ShieldCheck className="w-3 h-3 text-[#1E40AF]" />
              7-Dimension Quality Guard
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-[#F4F5F6] text-[#4A4E51] border border-[#E4E4E8]">
              No Float Arithmetic
            </span>
          </div>
        </div>
      </div>

      {/* Main Header Hero */}
      <div className="bg-white border-b border-[#E4E4E8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 rounded-lg bg-[#F4F5F6] border border-[#E4E4E8] text-[#1E2022]">
                  <Database className="w-5 h-5 text-[#1E2022]" />
                </div>
                <span className="text-xs font-semibold tracking-wider text-[#72777A] uppercase">
                  Institutional Financial Infrastructure • Prompt 03
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-[#1E2022] tracking-tight">
                Data Trust, Governance & PostgreSQL Architecture
              </h1>
              <p className="text-sm text-[#72777A] max-w-3xl mt-1.5 leading-relaxed">
                Single System of Record for sovereign reference data, bitemporal tax rulesets, institutional FX pipelines, 
                7-dimension automated data quality, and four-eyes regulatory change management.
              </p>
            </div>

            <div className="flex items-center gap-2.5 shrink-0">
              <button
                onClick={() => setActiveTab('master-specs')}
                className="px-3.5 py-2 text-xs font-medium bg-white text-[#1E2022] border border-[#E4E4E8] rounded-md hover:bg-[#F4F5F6] transition-colors cursor-pointer flex items-center gap-1.5"
              >
                <BookOpen className="w-3.5 h-3.5 text-[#72777A]" />
                Master Specs (A→AT)
              </button>
              <button
                onClick={() => setActiveTab('schemas')}
                className="px-3.5 py-2 text-xs font-medium bg-[#1E2022] text-white rounded-md hover:bg-[#313538] transition-colors cursor-pointer flex items-center gap-1.5 shadow-sm"
              >
                <FileCode2 className="w-3.5 h-3.5 text-white" />
                Inspect SQL DDL
              </button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar border-b border-[#E4E4E8] mt-8 pt-1">
            {[
              { id: 'overview', label: 'Command Center', icon: Activity },
              { id: 'schemas', label: 'PostgreSQL 18 Schemas', icon: Database },
              { id: 'quality', label: 'Data Quality & Quarantine', icon: ShieldCheck, badge: quarantineItems.filter(q => q.status === 'PENDING').length },
              { id: 'freshness', label: 'Freshness Engine & SLAs', icon: Clock },
              { id: 'lineage', label: 'Lineage & Provenance', icon: GitBranch },
              { id: 'coverage', label: 'Global Coverage Matrix', icon: Globe2 },
              { id: 'sources', label: 'Data Source Catalog', icon: Server },
              { id: 'governance', label: 'Four-Eyes & Audit', icon: Lock },
              { id: 'master-specs', label: 'Master Specs (A-AT)', icon: BookOpen }
            ].map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as GovernanceTab)}
                  className={`px-3.5 py-2.5 text-xs font-medium whitespace-nowrap transition-colors border-b-2 flex items-center gap-2 cursor-pointer ${
                    isActive
                      ? 'border-[#1E2022] text-[#1E2022] font-semibold bg-[#FBFBFC]'
                      : 'border-transparent text-[#72777A] hover:text-[#1E2022] hover:border-[#CBD0D5]'
                  }`}
                >
                  <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-[#1E2022]' : 'text-[#72777A]'}`} />
                  {tab.label}
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-[#FEF3F2] text-[#B42318] border border-[#FECDCA]">
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content Body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {resolvedNotice && (
          <div className="mb-6 p-3.5 rounded-lg bg-[#EBF8F2] border border-[#B3E8CE] text-[#0E7044] text-xs flex items-center justify-between shadow-sm animate-fade-in">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#0E7044]" />
              <span className="font-medium">{resolvedNotice}</span>
            </div>
            <button 
              onClick={() => setResolvedNotice(null)}
              className="text-[#0E7044] hover:underline cursor-pointer"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 1: COMMAND CENTER OVERVIEW */}
        {/* ========================================================================= */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* KPI Metrics Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-white border border-[#E4E4E8] shadow-xs">
                <div className="flex items-center justify-between text-xs text-[#72777A] mb-1.5">
                  <span className="font-medium">Ingestion Success Rate</span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#EBF8F2] text-[#0E7044]">
                    24h SLA
                  </span>
                </div>
                <div className="text-2xl font-semibold text-[#1E2022]">99.98%</div>
                <div className="flex items-center gap-1 text-[11px] text-[#0E7044] mt-1">
                  <CheckCircle2 className="w-3 h-3" />
                  <span>3,840 records processed today</span>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-white border border-[#E4E4E8] shadow-xs">
                <div className="flex items-center justify-between text-xs text-[#72777A] mb-1.5">
                  <span className="font-medium">Active Data Sources</span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#F0F4FF] text-[#1E40AF]">
                    Statutory
                  </span>
                </div>
                <div className="text-2xl font-semibold text-[#1E2022]">8 / 8 Healthy</div>
                <div className="flex items-center gap-1 text-[11px] text-[#72777A] mt-1">
                  <Server className="w-3 h-3 text-[#0E7044]" />
                  <span>ECB, Fed, BoE, IRS, HMRC, DGFiP, Wise, XE</span>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-white border border-[#E4E4E8] shadow-xs">
                <div className="flex items-center justify-between text-xs text-[#72777A] mb-1.5">
                  <span className="font-medium">Data Quality Pass Score</span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#EBF8F2] text-[#0E7044]">
                    7 Dimensions
                  </span>
                </div>
                <div className="text-2xl font-semibold text-[#1E2022]">99.92%</div>
                <div className="flex items-center gap-1 text-[11px] text-[#72777A] mt-1">
                  <ShieldCheck className="w-3 h-3 text-[#0E7044]" />
                  <span>Zero floating point rounding errors</span>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-white border border-[#E4E4E8] shadow-xs">
                <div className="flex items-center justify-between text-xs text-[#72777A] mb-1.5">
                  <span className="font-medium">Quarantine Triage</span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#FEF3F2] text-[#B42318]">
                    Live Queue
                  </span>
                </div>
                <div className="text-2xl font-semibold text-[#1E2022]">
                  {quarantineItems.filter(q => q.status === 'PENDING').length} Pending
                </div>
                <div className="flex items-center gap-1 text-[11px] text-[#B42318] mt-1">
                  <AlertTriangle className="w-3 h-3" />
                  <span>Requires operator review</span>
                </div>
              </div>
            </div>

            {/* Pipeline Architecture Visual Box */}
            <div className="p-6 rounded-xl bg-white border border-[#E4E4E8] shadow-xs">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-base font-semibold text-[#1E2022]">
                    End-to-End Financial Data Ingestion & Promotion Pipeline
                  </h3>
                  <p className="text-xs text-[#72777A] mt-0.5">
                    Isolated multi-tier pipeline preventing unvalidated external payloads from contaminating production calculations.
                  </p>
                </div>
                <button
                  onClick={() => setActiveTab('lineage')}
                  className="text-xs font-medium text-[#1E40AF] hover:underline flex items-center gap-1 cursor-pointer"
                >
                  View Lineage Trace
                  <ArrowRight className="w-3 h-3" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 relative">
                {[
                  {
                    step: '01. RAW INGEST',
                    title: 'Upstream Feeds',
                    desc: 'Statutory REST/XML feeds, SHA-256 payload hashing, immutable hot storage.',
                    badge: 'ingestion.raw_records',
                    color: 'border-[#CBD0D5]'
                  },
                  {
                    step: '02. STAGING GATE',
                    title: 'Zod Validation',
                    desc: 'Mathematical invariants, ISO check, boundary detection, anomaly filter.',
                    badge: 'Data Quality Engine',
                    color: 'border-[#94A3B8]'
                  },
                  {
                    step: '03. QUARANTINE',
                    title: 'Anomaly Triage',
                    desc: 'Isolates suspect spikes & malformed data without stopping valid flow.',
                    badge: 'ingestion.quarantine',
                    color: 'border-[#FECDCA] bg-[#FEF3F2]/40'
                  },
                  {
                    step: '04. CANONICAL',
                    title: 'PostgreSQL 18 SoR',
                    desc: 'Partitioned tables, bitemporal rulesets, NUMERIC(18,4) precision.',
                    badge: 'Single System of Record',
                    color: 'border-[#B3E8CE] bg-[#EBF8F2]/40'
                  },
                  {
                    step: '05. SERVING',
                    title: 'Materialized Views',
                    desc: 'Concurrent 60s cache, deterministic formula execution, Trust Badge.',
                    badge: 'Client Web Application',
                    color: 'border-[#C7D7FE] bg-[#F0F4FF]/40'
                  }
                ].map((stage, idx) => (
                  <div 
                    key={idx}
                    className={`p-3.5 rounded-lg border ${stage.color} flex flex-col justify-between`}
                  >
                    <div>
                      <div className="text-[10px] font-bold text-[#72777A] tracking-wider mb-1">
                        {stage.step}
                      </div>
                      <div className="text-xs font-semibold text-[#1E2022] mb-1">
                        {stage.title}
                      </div>
                      <p className="text-[11px] text-[#72777A] leading-normal">
                        {stage.desc}
                      </p>
                    </div>
                    <div className="mt-3 pt-2 border-t border-[#E4E4E8]/60 text-[10px] font-mono text-[#4A4E51]">
                      {stage.badge}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Two-Column Diagnostic Grid: Recent Ingestions & Active Tax Rules */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Ingestion Runs */}
              <div className="p-5 rounded-xl bg-white border border-[#E4E4E8] shadow-xs">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-[#1E2022]" />
                    <h3 className="text-sm font-semibold text-[#1E2022]">Live Ingestion Runs (Telemetry)</h3>
                  </div>
                  <button 
                    onClick={() => setActiveTab('freshness')}
                    className="text-xs text-[#1E40AF] hover:underline cursor-pointer"
                  >
                    View All SLAs
                  </button>
                </div>

                <div className="space-y-3">
                  {INGESTION_RUNS_DATA.map(run => (
                    <div 
                      key={run.id}
                      className="p-3 rounded-lg bg-[#FBFBFC] border border-[#E4E4E8] flex items-center justify-between text-xs"
                    >
                      <div className="space-y-0.5">
                        <div className="font-semibold text-[#1E2022] flex items-center gap-2">
                          <span>{run.sourceName}</span>
                          <span className={`px-1.5 py-0.2 rounded text-[10px] font-bold ${
                            run.status === 'SUCCESS' 
                              ? 'bg-[#EBF8F2] text-[#0E7044]' 
                              : 'bg-[#FEF3F2] text-[#B42318]'
                          }`}>
                            {run.status}
                          </span>
                        </div>
                        <div className="text-[11px] text-[#72777A]">
                          Domain: <span className="font-medium text-[#4A4E51]">{run.domain}</span> • Duration: {run.durationMs}ms
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="font-medium text-[#1E2022]">
                          {run.recordsAccepted} accepted / {run.recordsReceived} total
                        </div>
                        <div className="text-[10px] text-[#72777A]">{run.startedAt}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Certified Tax Rulesets */}
              <div className="p-5 rounded-xl bg-white border border-[#E4E4E8] shadow-xs">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-[#1E2022]" />
                    <h3 className="text-sm font-semibold text-[#1E2022]">Bitemporal Statutory Tax Rulesets</h3>
                  </div>
                  <button 
                    onClick={() => setActiveTab('governance')}
                    className="text-xs text-[#1E40AF] hover:underline cursor-pointer"
                  >
                    Maker/Checker Console
                  </button>
                </div>

                <div className="space-y-3">
                  {TAX_RULESETS_DATA.map(trs => (
                    <div 
                      key={trs.id}
                      className="p-3 rounded-lg bg-[#FBFBFC] border border-[#E4E4E8] flex items-center justify-between text-xs"
                    >
                      <div className="space-y-0.5">
                        <div className="font-semibold text-[#1E2022] flex items-center gap-2">
                          <span>{trs.jurisdictionName}</span>
                          <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-[#EBF8F2] text-[#0E7044]">
                            {trs.version}
                          </span>
                        </div>
                        <div className="text-[11px] text-[#72777A]">
                          Effective: {trs.effectiveFrom} to {trs.effectiveTo} • {trs.bracketsCount} progressive brackets
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-white border border-[#E4E4E8] text-[#0E7044]">
                          <Check className="w-3 h-3" />
                          Four-Eyes Certified
                        </div>
                        <div className="text-[10px] text-[#72777A] mt-0.5">{trs.sourceName.substring(0, 28)}...</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 2: POSTGRESQL 18 SCHEMAS & SQL DDL */}
        {/* ========================================================================= */}
        {activeTab === 'schemas' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-white border border-[#E4E4E8]">
              <div>
                <h3 className="text-sm font-semibold text-[#1E2022]">
                  PostgreSQL 18 System of Record DDL Definitions
                </h3>
                <p className="text-xs text-[#72777A] mt-0.5">
                  Universal database schemas with strict NUMERIC precision, foreign key cascades, and check constraints.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleCopySQL(POSTGRES_SCHEMAS_P3[selectedSchema].ddl)}
                  className="px-3 py-1.5 text-xs font-medium bg-[#1E2022] text-white rounded-md hover:bg-[#313538] transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  {copiedCode ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copiedCode ? 'Copied DDL!' : 'Copy SQL Table'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Table Selector Sidebar */}
              <div className="lg:col-span-4 space-y-2">
                <div className="text-xs font-bold text-[#72777A] uppercase px-1">
                  Core Tables ({POSTGRES_SCHEMAS_P3.length})
                </div>
                {POSTGRES_SCHEMAS_P3.map((table, idx) => (
                  <button
                    key={table.name}
                    onClick={() => setSelectedSchema(idx)}
                    className={`w-full text-left p-3 rounded-lg border transition-all cursor-pointer ${
                      selectedSchema === idx
                        ? 'bg-white border-[#1E2022] shadow-xs'
                        : 'bg-[#FBFBFC] border-[#E4E4E8] hover:bg-white hover:border-[#CBD0D5]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono font-semibold text-[#1E2022]">
                        {table.name}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.2 rounded font-mono bg-[#F4F5F6] text-[#72777A]">
                        {table.schema}
                      </span>
                    </div>
                    <p className="text-[11px] text-[#72777A] mt-1 line-clamp-2">
                      {table.description}
                    </p>
                  </button>
                ))}
              </div>

              {/* DDL Code Inspector */}
              <div className="lg:col-span-8">
                <div className="rounded-xl border border-[#E4E4E8] bg-white overflow-hidden shadow-xs">
                  <div className="px-4 py-3 bg-[#F4F5F6] border-b border-[#E4E4E8] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TableProperties className="w-4 h-4 text-[#1E2022]" />
                      <span className="text-xs font-mono font-semibold text-[#1E2022]">
                        {POSTGRES_SCHEMAS_P3[selectedSchema].name}.sql
                      </span>
                    </div>
                    <span className="text-[11px] text-[#72777A]">
                      PostgreSQL 18.x Strict Syntax
                    </span>
                  </div>

                  <div className="p-4 bg-[#1E2022] text-[#E4E4E8] font-mono text-xs overflow-x-auto">
                    <pre className="leading-relaxed">
                      <code>{POSTGRES_SCHEMAS_P3[selectedSchema].ddl}</code>
                    </pre>
                  </div>

                  <div className="p-4 bg-white border-t border-[#E4E4E8] text-xs text-[#72777A]">
                    <div className="font-semibold text-[#1E2022] mb-1">Architecture Notes & Invariants:</div>
                    <ul className="list-disc list-inside space-y-1 text-[11px]">
                      <li>Primary Keys use UUIDv7 for time-ordered index performance and sequential write speed.</li>
                      <li>Financial monetary quantities are strictly stored as <code className="text-[#1E2022] font-semibold">NUMERIC(18, 4)</code>.</li>
                      <li>Floating-point types (<code className="text-[#1E2022] font-semibold">REAL, FLOAT, DOUBLE PRECISION</code>) are forbidden by schema policy.</li>
                      <li>Foreign key constraints enforce referential integrity across all 12 domain schemas.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 3: DATA QUALITY ENGINE & QUARANTINE TRIAGE */}
        {/* ========================================================================= */}
        {activeTab === 'quality' && (
          <div className="space-y-8">
            {/* 7 Quality Dimensions Grid */}
            <div>
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-[#1E2022]">
                  The 7 Financial Data Quality Dimensions
                </h3>
                <p className="text-xs text-[#72777A] mt-0.5">
                  Automated verification rules executed on every single ingestion batch prior to canonical table promotion.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {DATA_QUALITY_RULES_DATA.map(rule => (
                  <div 
                    key={rule.id}
                    className="p-4 rounded-xl bg-white border border-[#E4E4E8] shadow-xs flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="font-mono text-[10px] text-[#72777A]">{rule.id}</span>
                        <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-[#EBF8F2] text-[#0E7044]">
                          {rule.dimension}
                        </span>
                      </div>
                      <div className="text-xs font-semibold text-[#1E2022] mb-1">
                        {rule.name}
                      </div>
                      <p className="text-[11px] text-[#72777A] leading-relaxed">
                        {rule.description}
                      </p>
                    </div>

                    <div className="mt-4 pt-3 border-t border-[#E4E4E8] flex items-center justify-between text-[11px]">
                      <span className="text-[#72777A]">Pass Rate:</span>
                      <span className="font-semibold text-[#0E7044]">{rule.currentPassRatePct}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Live Quarantine Triage Table */}
            <div className="p-6 rounded-xl bg-white border border-[#E4E4E8] shadow-xs">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-[#1E2022]">
                    Data Quarantine & Anomaly Triage Queue
                  </h3>
                  <p className="text-xs text-[#72777A] mt-0.5">
                    Holding area for suspicious volatility spikes, malformed ISO payloads, and failed validation assertions.
                  </p>
                </div>
                <span className="px-2 py-0.5 rounded text-xs font-semibold bg-[#FEF3F2] text-[#B42318] border border-[#FECDCA]">
                  {quarantineItems.filter(q => q.status === 'PENDING').length} Pending Operator Action
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-[#F4F5F6] text-[#72777A] border-y border-[#E4E4E8]">
                    <tr>
                      <th className="py-2.5 px-3 font-semibold">Quarantine ID</th>
                      <th className="py-2.5 px-3 font-semibold">Source</th>
                      <th className="py-2.5 px-3 font-semibold">Entity Type</th>
                      <th className="py-2.5 px-3 font-semibold">Anomaly / Failure Reason</th>
                      <th className="py-2.5 px-3 font-semibold">Status</th>
                      <th className="py-2.5 px-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E4E4E8]">
                    {quarantineItems.map(item => (
                      <tr key={item.id} className="hover:bg-[#FBFBFC] transition-colors">
                        <td className="py-3 px-3 font-mono font-medium text-[#1E2022]">
                          {item.id}
                        </td>
                        <td className="py-3 px-3 text-[#4A4E51]">
                          {item.sourceName}
                        </td>
                        <td className="py-3 px-3 font-mono text-[11px] text-[#72777A]">
                          {item.entityType}
                        </td>
                        <td className="py-3 px-3 text-[#B42318] max-w-xs">
                          {item.reason}
                        </td>
                        <td className="py-3 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            item.status === 'PENDING' ? 'bg-[#FEF3F2] text-[#B42318]' :
                            item.status === 'RESOLVED' ? 'bg-[#EBF8F2] text-[#0E7044]' :
                            'bg-[#F4F5F6] text-[#72777A]'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-right">
                          {item.status === 'PENDING' ? (
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => handleResolveQuarantine(item.id, 'RESOLVED')}
                                className="px-2 py-1 bg-[#0E7044] text-white rounded text-[11px] font-medium hover:bg-[#0B5936] transition-colors cursor-pointer"
                              >
                                Resolve & Promote
                              </button>
                              <button
                                onClick={() => handleResolveQuarantine(item.id, 'REJECTED')}
                                className="px-2 py-1 bg-white border border-[#E4E4E8] text-[#B42318] rounded text-[11px] font-medium hover:bg-[#FEF3F2] transition-colors cursor-pointer"
                              >
                                Discard
                              </button>
                            </div>
                          ) : (
                            <span className="text-[11px] text-[#72777A]">
                              By {item.resolvedBy?.split('@')[0]}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 4: FRESHNESS ENGINE & SLA POLICIES */}
        {/* ========================================================================= */}
        {activeTab === 'freshness' && (
          <div className="space-y-6">
            <div className="p-4 rounded-xl bg-white border border-[#E4E4E8]">
              <h3 className="text-sm font-semibold text-[#1E2022]">
                Domain-Specific Freshness Engine & Fallback Switchboard
              </h3>
              <p className="text-xs text-[#72777A] mt-0.5">
                Strict Time-to-Live (TTL) definitions governing real-time wholesale rates, statutory central bank benchmarks, and tax rules.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {FRESHNESS_POLICIES_DATA.map((policy, idx) => (
                <div 
                  key={idx}
                  className="p-5 rounded-xl bg-white border border-[#E4E4E8] shadow-xs flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="font-semibold text-[#1E2022]">{policy.domain}</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#EBF8F2] text-[#0E7044] border border-[#B3E8CE]">
                        {policy.status}
                      </span>
                    </div>
                    <div className="text-xs text-[#4A4E51] font-medium mb-3">
                      {policy.dataType}
                    </div>

                    <div className="space-y-1.5 text-[11px] text-[#72777A] bg-[#FBFBFC] p-3 rounded-lg border border-[#E4E4E8]">
                      <div className="flex justify-between">
                        <span>Fresh TTL:</span>
                        <span className="font-medium text-[#1E2022]">{policy.freshForMinutes} mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Warning After:</span>
                        <span className="font-medium text-[#1E2022]">{policy.warningAfterMinutes} mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Stale Threshold:</span>
                        <span className="font-medium text-[#B42318]">{policy.staleAfterMinutes} mins</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Fallback Allowed:</span>
                        <span className="font-medium text-[#1E2022]">{policy.fallbackAllowed ? 'Yes (LKG)' : 'No (Strict)'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-[#E4E4E8] text-[11px] flex items-center justify-between">
                    <span className="text-[#72777A]">Last Update:</span>
                    <span className="font-medium text-[#0E7044]">{policy.lastUpdated}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Fallback Failover Mechanism Spec Box */}
            <div className="p-5 rounded-xl bg-[#F0F4FF] border border-[#C7D7FE] text-xs text-[#1E40AF]">
              <div className="font-semibold text-sm mb-1 flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-[#1E40AF]" />
                Automated Last Known Good (LKG) Failover Architecture
              </div>
              <p className="leading-relaxed text-[#1E3A8A]">
                When an upstream provider experiences a network disruption or breaches freshness SLAs, YamooPay never displays broken screens. 
                Instead, the engine seamlessly engages verified secondary feeds or the Last Known Good snapshot while explicitly flagging 
                the timestamp in the UI trust drawer.
              </p>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 5: LINEAGE & PROVENANCE INSPECTOR */}
        {/* ========================================================================= */}
        {activeTab === 'lineage' && (
          <div className="space-y-6">
            <div className="p-4 rounded-xl bg-white border border-[#E4E4E8]">
              <h3 className="text-sm font-semibold text-[#1E2022]">
                Interactive Financial Data Lineage Inspector
              </h3>
              <p className="text-xs text-[#72777A] mt-0.5">
                Step-by-step cryptographic audit trace illustrating how an external reference tick transforms into a user-facing calculation.
              </p>
            </div>

            <div className="relative border-l-2 border-[#CBD0D5] ml-4 sm:ml-6 pl-6 sm:pl-8 space-y-6">
              {SAMPLE_DATA_LINEAGE_NODES.map((node, idx) => (
                <div key={idx} className="relative">
                  {/* Step Dot */}
                  <div className="absolute -left-[35px] sm:-left-[43px] top-1.5 w-6 h-6 rounded-full bg-white border-2 border-[#1E2022] flex items-center justify-center text-[10px] font-bold text-[#1E2022] shadow-xs">
                    {idx + 1}
                  </div>

                  <div className="p-4 rounded-xl bg-white border border-[#E4E4E8] shadow-xs">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#F4F5F6] text-[#4A4E51]">
                          {node.level}
                        </span>
                        <h4 className="text-sm font-semibold text-[#1E2022]">{node.title}</h4>
                      </div>
                      <span className="text-[11px] text-[#72777A] font-mono">{node.timestamp}</span>
                    </div>

                    <p className="text-xs text-[#4A4E51] mb-3">
                      {node.description}
                    </p>

                    <div className="p-2.5 rounded-lg bg-[#FBFBFC] border border-[#E4E4E8] font-mono text-[11px] text-[#1E2022] space-y-1">
                      <div className="text-[#72777A]">Source Ref: <span className="text-[#1E2022]">{node.sourceRef}</span></div>
                      {Object.entries(node.metadata).map(([key, val]) => (
                        <div key={key} className="flex justify-between text-[10px]">
                          <span className="text-[#72777A]">{key}:</span>
                          <span className="font-semibold text-[#4A4E51]">{val}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 6: GLOBAL COVERAGE & MARKET READINESS */}
        {/* ========================================================================= */}
        {activeTab === 'coverage' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-white border border-[#E4E4E8]">
              <div>
                <h3 className="text-sm font-semibold text-[#1E2022]">
                  Global Coverage & 5-Gate Market Readiness Matrix
                </h3>
                <p className="text-xs text-[#72777A] mt-0.5">
                  Tracking statutory certification and tool readiness across 35+ countries and 5 strategic rollout waves.
                </p>
              </div>
              <div className="text-xs text-[#72777A]">
                Rule: <span className="font-semibold text-[#1E2022]">Correctness Before Coverage</span>
              </div>
            </div>

            <div className="p-6 rounded-xl bg-white border border-[#E4E4E8] shadow-xs overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-[#F4F5F6] text-[#72777A] border-y border-[#E4E4E8]">
                  <tr>
                    <th className="py-2.5 px-3 font-semibold">Country</th>
                    <th className="py-2.5 px-3 font-semibold">Wave</th>
                    <th className="py-2.5 px-3 font-semibold">Readiness Score</th>
                    <th className="py-2.5 px-3 font-semibold">Tax Certification</th>
                    <th className="py-2.5 px-3 font-semibold">FX Feed</th>
                    <th className="py-2.5 px-3 font-semibold">Supported Tools</th>
                    <th className="py-2.5 px-3 font-semibold text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E4E4E8]">
                  {GLOBAL_MARKET_READINESS_DATA.map(market => (
                    <tr key={market.countryCode} className="hover:bg-[#FBFBFC] transition-colors">
                      <td className="py-3 px-3 font-medium text-[#1E2022] flex items-center gap-2">
                        <span className="text-base">{market.flag}</span>
                        <span>{market.countryName} ({market.currency})</span>
                      </td>
                      <td className="py-3 px-3 text-[#72777A]">
                        Wave {market.wave}
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-[#E4E4E8] h-1.5 rounded-full overflow-hidden">
                            <div 
                              className="bg-[#0E7044] h-full rounded-full" 
                              style={{ width: `${market.readinessScore}%` }}
                            />
                          </div>
                          <span className="font-semibold text-[#1E2022]">{market.readinessScore}%</span>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          market.taxRulesStatus === 'CERTIFIED' 
                            ? 'bg-[#EBF8F2] text-[#0E7044]' 
                            : 'bg-[#FEF3F2] text-[#B42318]'
                        }`}>
                          {market.taxRulesStatus}
                        </span>
                      </td>
                      <td className="py-3 px-3 font-mono text-[11px] text-[#4A4E51]">
                        {market.fxStatus}
                      </td>
                      <td className="py-3 px-3 text-[11px] text-[#72777A]">
                        {Object.entries(market.toolsSupported)
                          .filter(([_, enabled]) => enabled)
                          .map(([tool]) => tool)
                          .join(', ')}
                      </td>
                      <td className="py-3 px-3 text-right">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          market.launchStatus === 'LIVE' ? 'bg-[#EBF8F2] text-[#0E7044]' :
                          market.launchStatus === 'BETA' ? 'bg-[#F0F4FF] text-[#1E40AF]' :
                          'bg-[#F4F5F6] text-[#72777A]'
                        }`}>
                          {market.launchStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 7: DATA SOURCE CATALOG */}
        {/* ========================================================================= */}
        {activeTab === 'sources' && (
          <div className="space-y-6">
            <div className="p-4 rounded-xl bg-white border border-[#E4E4E8]">
              <h3 className="text-sm font-semibold text-[#1E2022]">
                Official Statutory & Institutional Data Providers
              </h3>
              <p className="text-xs text-[#72777A] mt-0.5">
                Every data source is registered with explicit licensing terms, authority ranking, and automated health monitoring.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {DATA_SOURCES_CATALOG.map(source => (
                <div 
                  key={source.id}
                  className="p-5 rounded-xl bg-white border border-[#E4E4E8] shadow-xs flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#F4F5F6] text-[#4A4E51]">
                        {source.type}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#0E7044]">
                        <span className="w-2 h-2 rounded-full bg-[#0E7044]"></span>
                        {source.currentStatus} ({source.reliabilityScore}%)
                      </span>
                    </div>

                    <h4 className="text-sm font-semibold text-[#1E2022] mb-1">
                      {source.name}
                    </h4>
                    <div className="text-xs text-[#72777A] mb-3">
                      Provider: <span className="font-medium text-[#4A4E51]">{source.provider}</span> • Domain: {source.domain}
                    </div>

                    <div className="space-y-1 text-[11px] text-[#72777A] bg-[#FBFBFC] p-3 rounded-lg border border-[#E4E4E8]">
                      <div>Authority Level: <span className="font-semibold text-[#1E2022]">{source.authorityLevel}</span></div>
                      <div>License: <span className="text-[#4A4E51]">{source.licenseType}</span></div>
                      <div>Refresh: <span className="text-[#4A4E51]">{source.refreshStrategy}</span></div>
                      <div>Endpoint: <span className="font-mono text-[10px] text-[#1E2022]">{source.baseUrl}</span></div>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-[#E4E4E8] flex items-center justify-between text-[11px]">
                    <span className="text-[#72777A]">Latency: {source.latencyMs}ms</span>
                    <a 
                      href={source.officialWebsite} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#1E40AF] hover:underline flex items-center gap-1"
                    >
                      Official Portal
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 8: FOUR-EYES GOVERNANCE & AUDIT TRAIL */}
        {/* ========================================================================= */}
        {activeTab === 'governance' && (
          <div className="space-y-6">
            <div className="p-4 rounded-xl bg-white border border-[#E4E4E8]">
              <h3 className="text-sm font-semibold text-[#1E2022]">
                Four-Eyes Principle (Maker / Checker) & Append-Only Audit Ledger
              </h3>
              <p className="text-xs text-[#72777A] mt-0.5">
                Statutory tax rulesets and calculation parameters require dual authorization from two distinct individuals before reaching production.
              </p>
            </div>

            <div className="space-y-4">
              {TAX_RULESETS_DATA.map(trs => (
                <div 
                  key={trs.id}
                  className="p-5 rounded-xl bg-white border border-[#E4E4E8] shadow-xs"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded text-xs font-bold bg-[#EBF8F2] text-[#0E7044]">
                        {trs.status}
                      </span>
                      <h4 className="text-sm font-semibold text-[#1E2022]">
                        {trs.jurisdictionName} — {trs.taxYear} ({trs.version})
                      </h4>
                    </div>
                    <span className="text-xs text-[#72777A]">
                      Effective: {trs.effectiveFrom} to {trs.effectiveTo}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3.5 rounded-lg bg-[#FBFBFC] border border-[#E4E4E8] text-xs">
                    <div>
                      <div className="text-[10px] font-bold text-[#72777A] uppercase mb-0.5">Maker (Drafted)</div>
                      <div className="font-medium text-[#1E2022]">{trs.makerChecker.createdBy}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-[#72777A] uppercase mb-0.5">Checker (Verified)</div>
                      <div className="font-medium text-[#1E2022]">{trs.makerChecker.reviewedBy}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-[#72777A] uppercase mb-0.5">Approver (Signed)</div>
                      <div className="font-medium text-[#0E7044]">{trs.makerChecker.approvedBy}</div>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between text-[11px] text-[#72777A]">
                    <span>Source Authority: <strong className="text-[#4A4E51]">{trs.sourceName}</strong></span>
                    <span>Verified: {trs.verifiedAt}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 9: MASTER SPECIFICATIONS (A THROUGH AT) */}
        {/* ========================================================================= */}
        {activeTab === 'master-specs' && (
          <div className="space-y-6">
            <div className="p-4 rounded-xl bg-white border border-[#E4E4E8] flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-semibold text-[#1E2022]">
                  Master Architecture Specifications (Deliverables A through AT)
                </h3>
                <p className="text-xs text-[#72777A] mt-0.5">
                  Complete technical specification covering all 46 enterprise deliverables for PostgreSQL data governance.
                </p>
              </div>

              {/* Category Filter */}
              <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                {['ALL', 'Strategy & Domains', 'PostgreSQL & Schema', 'Pipeline & Quality', 'Governance & Security', 'Operations & Runbooks'].map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      selectedCategory === cat
                        ? 'bg-[#1E2022] text-white'
                        : 'bg-[#F4F5F6] text-[#72777A] hover:text-[#1E2022]'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search className="w-4 h-4 text-[#72777A] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search across 46 deliverables by keyword, deliverable letter, or domain..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-white border border-[#E4E4E8] rounded-lg text-xs focus:outline-none focus:border-[#1E2022]"
              />
            </div>

            {/* Deliverables List */}
            <div className="space-y-4">
              {filteredDeliverables.map(del => (
                <div 
                  key={del.code}
                  className="p-5 rounded-xl bg-white border border-[#E4E4E8] shadow-xs"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-lg bg-[#F4F5F6] border border-[#E4E4E8] flex items-center justify-center font-bold text-xs text-[#1E2022]">
                        {del.code}
                      </span>
                      <h4 className="text-sm font-semibold text-[#1E2022]">
                        {del.titleEn} <span className="text-[#72777A] font-normal">({del.titleFr})</span>
                      </h4>
                    </div>
                    <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-[#F4F5F6] text-[#4A4E51]">
                      {del.category}
                    </span>
                  </div>

                  <p className="text-xs text-[#4A4E51] mb-3 leading-relaxed">
                    {del.summary}
                  </p>

                  <div className="space-y-1.5 mb-3">
                    {del.details.map((detail, dIdx) => (
                      <div key={dIdx} className="flex items-start gap-2 text-xs text-[#72777A]">
                        <ChevronRight className="w-3.5 h-3.5 text-[#1E2022] shrink-0 mt-0.5" />
                        <span>{detail}</span>
                      </div>
                    ))}
                  </div>

                  <div className="p-2.5 rounded-lg bg-[#FBFBFC] border border-[#E4E4E8] text-[11px] text-[#72777A]">
                    <strong className="text-[#1E2022]">Architectural Invariant: </strong>
                    {del.architectureNotes}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
